import { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useCountry } from "../../contexts/CountryContext";

/**
 * Floating "Change country" pill — used in Navbar (compact) and Footer
 * (text). Mirrors LanguagePicker UX.
 *
 *   variant="compact"  → small pill with flag + country code (header use)
 *   variant="text"     → text-only link (footer use)
 *   variant="full"     → wider pill with country label (mobile / shop hero)
 */
export default function CountryPill({ variant = "compact" }) {
  const ctx = useCountry();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!ctx) return null;
  const { country, supported, setCountry, openPicker } = ctx;
  const active = supported.find((s) => s.code === country) || supported[0];

  if (variant === "text") {
    return (
      <button
        type="button"
        onClick={openPicker}
        className="inline-flex items-center gap-1.5 text-emerald-900/70 hover:text-emerald-700 transition"
        data-testid="country-pill-text"
      >
        <Globe className="w-3.5 h-3.5" />
        {active?.label || country} <span className="text-emerald-900/40">·</span> {active?.currency || ""}
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-emerald-900 hover:border-emerald-300 hover:bg-emerald-50/60 transition ${variant === "full" ? "px-4 py-2 text-sm" : ""}`}
        data-testid="country-pill"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="w-3.5 h-3.5 text-emerald-700" />
        <span>{variant === "full" ? (active?.label || country) : (country || "—")}</span>
        <ChevronDown className="w-3 h-3 text-emerald-700/70" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-52 rounded-2xl border border-emerald-100 bg-white shadow-lg py-1.5 z-50"
          role="listbox"
          data-testid="country-pill-dropdown"
        >
          {(supported || []).map((c) => (
            <button
              key={c.code}
              type="button"
              role="option"
              aria-selected={c.code === country}
              onClick={() => { setCountry(c.code); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3.5 py-2 text-sm text-left transition ${c.code === country ? "bg-emerald-50/60 text-emerald-900 font-semibold" : "text-emerald-900/85 hover:bg-emerald-50/40"}`}
              data-testid={`country-pill-option-${c.code}`}
            >
              <span>
                <span className="block">{c.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-emerald-900/55">Pricing in {c.currency}</span>
              </span>
              {c.code === country && <Check className="w-4 h-4 text-emerald-700" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
